import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SectionHelp from '@/components/help/SectionHelp';
import HelpTooltip from '@/components/help/HelpTooltip';
import { abrirParaImprimir } from '@/lib/imprimir';
import { Plus, X, Search, ClipboardList, CheckCircle2, AlertTriangle, Printer } from 'lucide-react';

// S120 (5/N) — Taller: el tablero y la orden de servicio.
//
// Un taller no vende coches, vende reparaciones. Y la reparación tiene un
// recorrido con un paso que decide todo: la autorización del cliente. Esta
// pantalla está armada alrededor de ese paso.

const COLORES = {
  recibido: 'bg-slate-100 text-slate-700',
  diagnostico: 'bg-blue-100 text-blue-800',
  presupuesto: 'bg-amber-100 text-amber-800',
  autorizado: 'bg-emerald-100 text-emerald-800',
  reparando: 'bg-indigo-100 text-indigo-800',
  listo: 'bg-teal-100 text-teal-800',
  entregado: 'bg-slate-100 text-slate-500',
  rechazado: 'bg-rose-100 text-rose-700',
  cancelado: 'bg-rose-100 text-rose-700',
};

const CANALES = [
  { k: 'whatsapp', t: 'Por WhatsApp' },
  { k: 'telefono', t: 'Por teléfono' },
  { k: 'mostrador', t: 'En el mostrador' },
  { k: 'correo', t: 'Por correo' },
];

function Etiqueta({ orden }) {
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${COLORES[orden.estado] || 'bg-slate-100'}`}>
      {orden.etiqueta}
    </span>
  );
}

const ORDEN_VACIA = {
  customer_name: '', customer_phone: '', license_plate: '', brand: '',
  model: '', year: '', color: '', mileage_km: '', reported_issue: '',
};

export default function WorkshopDesk() {
  const [cargando, setCargando] = useState(true);
  const [tablero, setTablero] = useState(null);
  const [abierta, setAbierta] = useState(null);   // detalle de una orden
  const [nueva, setNueva] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [renglon, setRenglon] = useState(null);
  const [autorizando, setAutorizando] = useState(null);
  const [equipo, setEquipo] = useState([]);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      // El equipo va en la MISMA carga y con .catch propio: si el listado de
      // equipo fallara, el tablero del taller —que es lo que el jefe abre en la
      // mañana— no puede quedarse en blanco por eso.
      const [tab, gente] = await Promise.all([
        api.get('/tenant/taller/tablero'),
        api.get('/tenant/team').catch(() => ({ data: [] })),
      ]);
      const data = tab.data;
      setEquipo(gente.data?.items || gente.data || []);
      setTablero(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const abrir = async (id) => {
    try {
      const { data } = await api.get(`/tenant/taller/ordenes/${id}`);
      setAbierta(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const recibir = async () => {
    if (!nueva.customer_name.trim()) { toast.error('Falta el nombre del cliente.'); return; }
    if (!nueva.license_plate.trim()) { toast.error('Falta la placa del vehículo.'); return; }
    try {
      const cuerpo = { ...nueva };
      ['year', 'mileage_km'].forEach(k => {
        cuerpo[k] = cuerpo[k] === '' ? null : Number(cuerpo[k]);
      });
      const { data } = await api.post('/tenant/taller/ordenes', cuerpo);
      toast.success(`Orden ${data.folio} creada.`);
      setNueva(null);
      await cargar();
      await abrir(data.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const agregarRenglon = async () => {
    if (!renglon.description.trim()) { toast.error('Falta decir qué es el renglón.'); return; }
    try {
      await api.post(`/tenant/taller/ordenes/${abierta.id}/renglones`, {
        description: renglon.description,
        unit_price: Number(renglon.unit_price) || 0,
        quantity: Number(renglon.quantity) || 1,
        kind: renglon.kind,
      });
      setRenglon(null);
      await abrir(abierta.id);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const quitarRenglon = async (id) => {
    try {
      await api.delete(`/tenant/taller/renglones/${id}`);
      await abrir(abierta.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const mover = async (estado) => {
    try {
      await api.post(`/tenant/taller/ordenes/${abierta.id}/estado`, { status: estado });
      await abrir(abierta.id);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // S135 — el presupuesto en papel, para que el cliente lo firme. Sale en
  // hoja carta y no en ticket: esto se archiva, y es el papel que se busca
  // meses después cuando alguien reclama que no autorizó un trabajo.
  const imprimirOrden = async () => {
    try {
      const { data } = await api.get(`/tenant/taller/ordenes/${abierta.id}/imprimir`);
      abrirParaImprimir(data.html, { titulo: `Orden ${data.folio || ''}` });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // S136 — quién repara. La lista es del equipo de ESTE taller; el servidor
  // además comprueba que la persona sea de aquí, porque una lista del frontend
  // no impide que alguien mande otro id por la API.
  const asignarTecnico = async (quien) => {
    try {
      const { data } = await api.post(`/tenant/taller/ordenes/${abierta.id}/tecnico`, {
        technician_user_id: quien || null,
      });
      setAbierta({ ...abierta, tecnico_id: data?.orden?.tecnico_id || null });
      toast.success(quien ? 'Técnico asignado.' : 'Se quitó el técnico.');
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const guardarDiagnostico = async () => {
    try {
      await api.put(`/tenant/taller/ordenes/${abierta.id}/diagnostico`, {
        diagnosis: abierta.diagnostico || '', notes: abierta.notas || '',
      });
      toast.success('Diagnóstico guardado.');
      await abrir(abierta.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const autorizar = async () => {
    try {
      const marcados = Object.entries(autorizando.marcados)
        .filter(([, v]) => v).map(([k]) => k);
      const { data } = await api.post(`/tenant/taller/ordenes/${abierta.id}/autorizar`, {
        channel: autorizando.canal,
        // null = autorizó todo. Una lista = solo esos.
        item_ids: marcados.length === (abierta.renglones || []).length ? null : marcados,
        note: autorizando.nota || null,
      });
      toast.success(data.mensaje);
      setAutorizando(null);
      await abrir(abierta.id);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const buscar = async () => {
    if (!busqueda.trim()) { await cargar(); return; }
    try {
      const { data } = await api.get('/tenant/taller/ordenes', { params: { buscar: busqueda } });
      setTablero({ ...(tablero || {}), ordenes: data.ordenes, abiertas: data.ordenes.length,
                   por_etapa: [], atrasadas: [] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ── Detalle de una orden ───────────────────────────────────────────
  if (abierta) {
    const renglones = abierta.renglones || [];
    const yaAutorizo = !!abierta.autorizado_el;
    return (
      <div className="space-y-4" data-testid="taller-orden">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold">Orden {abierta.folio}</h2>
            <p className="text-sm text-muted-foreground">
              {abierta.cliente} · {abierta.placa}
              {abierta.marca ? ` · ${abierta.marca} ${abierta.modelo || ''}` : ''}
              {abierta.kilometraje ? ` · ${abierta.kilometraje} km` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Etiqueta orden={abierta} />
            <Button size="sm" variant="ghost" onClick={() => { setAbierta(null); cargar(); }}>
              Volver al tablero
            </Button>
          </div>
        </div>

        {abierta.falla_reportada && (
          <div className="rounded border p-3">
            <p className="text-xs text-muted-foreground">Lo que reportó el cliente</p>
            <p className="text-sm">{abierta.falla_reportada}</p>
          </div>
        )}

        <div className="rounded border p-3 space-y-2">
          <label className="text-sm font-medium flex items-center gap-1">
            Quién lo está reparando
            <HelpTooltip text="El mecánico responsable de este coche. Sirve para saber a quién preguntarle y para ver la carga de trabajo de cada uno." />
          </label>
          <Select value={abierta.tecnico_id || 'nadie'}
            onValueChange={(v) => asignarTecnico(v === 'nadie' ? null : v)}>
            <SelectTrigger data-testid="taller-tecnico">
              <SelectValue placeholder="Sin asignar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="nadie">Sin asignar</SelectItem>
              {(equipo || []).map((m) => (
                <SelectItem key={m.id} value={m.id}>{m.name || m.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(equipo || []).length === 0 && (
            <p className="text-[11px] text-muted-foreground">
              Todavía no tienes equipo dado de alta. Agrégalo en Configuración →
              Equipo y aquí aparecerá para asignarlo.
            </p>
          )}
        </div>

        <div className="rounded border p-3 space-y-2">
          <label className="text-sm font-medium flex items-center gap-1">
            Diagnóstico del mecánico
            <HelpTooltip text="Lo que de verdad tiene el coche. Se guarda aparte de lo que reportó el cliente porque casi nunca es lo mismo, y esa diferencia es la que hay que explicarle." />
          </label>
          <Textarea rows={3} value={abierta.diagnostico || ''}
            onChange={e => setAbierta({ ...abierta, diagnostico: e.target.value })} />
          <Button size="sm" variant="outline" onClick={guardarDiagnostico}>Guardar diagnóstico</Button>
          <div className="pt-2">
            <HelpTooltip text="Saca el presupuesto en hoja para que el cliente lo firme: cómo llegó el coche (kilometraje y gasolina), qué reportó, qué encontró el taller, y renglón por renglón qué queda autorizado y qué no.">
              <Button size="sm" variant="outline" onClick={imprimirOrden}>
                <Printer className="h-4 w-4 mr-1" />Imprimir para firma
              </Button>
            </HelpTooltip>
          </div>
        </div>

        {/* Presupuesto */}
        <div className="rounded border">
          <div className="px-3 py-2 border-b flex items-center justify-between">
            <p className="text-sm font-medium">Presupuesto</p>
            {!yaAutorizo && (
              <Button size="sm" variant="outline"
                onClick={() => setRenglon({ description: '', unit_price: '', quantity: 1, kind: 'mano_obra' })}>
                <Plus className="h-4 w-4 mr-1" />Agregar renglón
              </Button>
            )}
          </div>

          {renglon && (
            <div className="p-3 border-b grid gap-2 sm:grid-cols-5 items-end">
              <div className="sm:col-span-2">
                <label className="text-xs">Qué es</label>
                <Input value={renglon.description} placeholder="Cambio de balatas delanteras"
                  onChange={e => setRenglon({ ...renglon, description: e.target.value })} />
              </div>
              <div>
                <label className="text-xs">Tipo</label>
                <Select value={renglon.kind} onValueChange={v => setRenglon({ ...renglon, kind: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mano_obra">Mano de obra</SelectItem>
                    <SelectItem value="refaccion">Refacción</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs">Cantidad</label>
                <Input type="number" min="1" value={renglon.quantity}
                  onChange={e => setRenglon({ ...renglon, quantity: e.target.value })} />
              </div>
              <div>
                <label className="text-xs">Precio</label>
                <Input type="number" value={renglon.unit_price}
                  onChange={e => setRenglon({ ...renglon, unit_price: e.target.value })} />
              </div>
              <div className="flex gap-2 sm:col-span-5">
                <Button size="sm" onClick={agregarRenglon}>Guardar</Button>
                <Button size="sm" variant="ghost" onClick={() => setRenglon(null)}>Cancelar</Button>
              </div>
            </div>
          )}

          {renglones.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">
              Todavía no hay presupuesto. Agrega la mano de obra y las refacciones
              para poder pedirle autorización al cliente.
            </p>
          ) : renglones.map(r => (
            <div key={r.id} className="p-3 border-b last:border-0 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm">{r.descripcion}</p>
                <p className="text-xs text-muted-foreground">
                  {r.tipo === 'refaccion' ? 'Refacción' : 'Mano de obra'} ·
                  {' '}{r.cantidad} × {r.precio_unitario}
                  {r.estado === 'rechazado' ? ' · el cliente no la autorizó' : ''}
                  {r.estado === 'autorizado' ? ' · autorizada' : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-sm font-medium">{r.importe}</span>
                {!yaAutorizo && (
                  <Button size="sm" variant="ghost" onClick={() => quitarRenglon(r.id)}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}

          {renglones.length > 0 && (
            <div className="p-3 border-t flex items-center justify-between">
              <div className="text-sm">
                <p>Cotizado: <span className="font-semibold">{abierta.total_cotizado}</span></p>
                {yaAutorizo && (
                  <p className="text-emerald-700">
                    Autorizado: <span className="font-semibold">{abierta.total_autorizado}</span>
                    {' '}— {abierta.autorizado_por}, {new Date(abierta.autorizado_el).toLocaleString()}
                  </p>
                )}
              </div>
              {!yaAutorizo && (
                <Button size="sm" onClick={() => setAutorizando({
                  canal: 'whatsapp', nota: '',
                  marcados: Object.fromEntries(renglones.map(r => [r.id, true])),
                })}>
                  <CheckCircle2 className="h-4 w-4 mr-1" />Registrar autorización
                </Button>
              )}
            </div>
          )}
        </div>

        {autorizando && (
          <div className="rounded border p-3 space-y-3">
            <div className="flex items-center justify-between">
              <p className="font-medium flex items-center gap-1">
                Autorización del cliente
                <HelpTooltip text="Queda registrado por dónde y cuándo autorizó. Es lo único que responde si después dice que nunca autorizó ese trabajo." />
              </p>
              <Button size="sm" variant="ghost" onClick={() => setAutorizando(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Desmarca lo que el cliente NO autorizó. Eso no se repara ni se cobra.
            </p>
            {renglones.map(r => (
              <label key={r.id} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!autorizando.marcados[r.id]}
                  onChange={e => setAutorizando({
                    ...autorizando,
                    marcados: { ...autorizando.marcados, [r.id]: e.target.checked },
                  })} />
                {r.descripcion} — {r.importe}
              </label>
            ))}
            <div className="grid gap-2 sm:grid-cols-2">
              <div>
                <label className="text-xs">¿Por dónde autorizó?</label>
                <Select value={autorizando.canal}
                  onValueChange={v => setAutorizando({ ...autorizando, canal: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CANALES.map(c => <SelectItem key={c.k} value={c.k}>{c.t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs">Nota (opcional)</label>
                <Input value={autorizando.nota} placeholder="Dijo que sí a las 10:15"
                  onChange={e => setAutorizando({ ...autorizando, nota: e.target.value })} />
              </div>
            </div>
            <Button onClick={autorizar}>Registrar autorización</Button>
          </div>
        )}

        {(abierta.siguientes || []).length > 0 && (
          <div className="rounded border p-3">
            <p className="text-sm font-medium mb-2">Mover la orden a…</p>
            <div className="flex gap-2 flex-wrap">
              {abierta.siguientes.map(s => (
                <Button key={s.estado} size="sm" variant="outline" onClick={() => mover(s.estado)}>
                  {s.etiqueta}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Tablero ────────────────────────────────────────────────────────
  return (
    <div className="space-y-4" data-testid="taller-tablero">
      <SectionHelp
        module="taller"
        title="Órdenes de servicio"
        description={'Aquí vive el trabajo del taller: se recibe el coche, se diagnostica, se '
            + 'cotiza, el cliente autoriza y se entrega. El paso de la autorización queda '
            + 'registrado con fecha y por dónde autorizó — es lo que te respalda si después '
            + 'dice que nunca lo aprobó. Si tienes la IA en WhatsApp, ella contesta sola el '
            + '«¿ya está mi carro?» con el estado real.'}
      />

      <div className="flex gap-2 flex-wrap items-center">
        <Input placeholder="Buscar por placa o folio…" value={busqueda} className="max-w-xs"
          onChange={e => setBusqueda(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && buscar()} />
        <Button size="sm" variant="outline" onClick={buscar}>
          <Search className="h-4 w-4 mr-1" />Buscar
        </Button>
        <Button size="sm" onClick={() => setNueva({ ...ORDEN_VACIA })}>
          <Plus className="h-4 w-4 mr-1" />Recibir un coche
        </Button>
      </div>

      {nueva && (
        <div className="rounded border p-3 grid gap-2 sm:grid-cols-4 items-end">
          <div>
            <label className="text-xs">Cliente</label>
            <Input value={nueva.customer_name}
              onChange={e => setNueva({ ...nueva, customer_name: e.target.value })} />
          </div>
          <div>
            <label className="text-xs">Teléfono</label>
            <Input value={nueva.customer_phone}
              onChange={e => setNueva({ ...nueva, customer_phone: e.target.value })} />
          </div>
          <div>
            <label className="text-xs flex items-center gap-1">Placa
              <HelpTooltip text="Es como se identifica el coche en el taller y como lo busca la IA cuando el cliente pregunta." /></label>
            <Input value={nueva.license_plate} placeholder="ABC-123"
              onChange={e => setNueva({ ...nueva, license_plate: e.target.value })} />
          </div>
          <div>
            <label className="text-xs">Kilometraje</label>
            <Input type="number" value={nueva.mileage_km}
              onChange={e => setNueva({ ...nueva, mileage_km: e.target.value })} />
          </div>
          <div>
            <label className="text-xs">Marca</label>
            <Input value={nueva.brand} placeholder="Nissan"
              onChange={e => setNueva({ ...nueva, brand: e.target.value })} />
          </div>
          <div>
            <label className="text-xs">Modelo</label>
            <Input value={nueva.model} placeholder="Versa"
              onChange={e => setNueva({ ...nueva, model: e.target.value })} />
          </div>
          <div>
            <label className="text-xs">Año</label>
            <Input type="number" value={nueva.year}
              onChange={e => setNueva({ ...nueva, year: e.target.value })} />
          </div>
          <div>
            <label className="text-xs">Color</label>
            <Input value={nueva.color}
              onChange={e => setNueva({ ...nueva, color: e.target.value })} />
          </div>
          <div className="sm:col-span-4">
            <label className="text-xs">¿Qué reporta el cliente?</label>
            <Textarea rows={2} value={nueva.reported_issue}
              onChange={e => setNueva({ ...nueva, reported_issue: e.target.value })} />
          </div>
          <div className="flex gap-2 sm:col-span-4">
            <Button size="sm" onClick={recibir}>Recibir el coche</Button>
            <Button size="sm" variant="ghost" onClick={() => setNueva(null)}>Cancelar</Button>
          </div>
        </div>
      )}

      {cargando && <p className="text-sm text-muted-foreground">Cargando…</p>}

      {!cargando && tablero && (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            {[
              ['Coches en el taller', tablero.abiertas ?? 0, ClipboardList],
              ['Esperando autorización', tablero.esperando_autorizacion ?? 0, AlertTriangle],
              ['Listos para entregar', tablero.listos_para_entregar ?? 0, CheckCircle2],
            ].map(([t, v, Icono]) => (
              <div key={t} className="rounded border p-3 flex items-center gap-3">
                <Icono className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">{t}</p>
                  <p className="text-2xl font-semibold">{v}</p>
                </div>
              </div>
            ))}
          </div>

          {(tablero.atrasadas || []).length > 0 && (
            <div className="rounded border border-amber-200 bg-amber-50 p-3">
              <p className="text-sm font-medium">
                {tablero.atrasadas.length} {tablero.atrasadas.length === 1
                  ? 'coche pasó' : 'coches pasaron'} de la fecha que prometiste
              </p>
              <p className="text-xs text-muted-foreground mb-2">
                Es lo que más enoja a un cliente y lo que un taller nunca ve a tiempo.
              </p>
              {tablero.atrasadas.map(o => (
                <button key={o.id} onClick={() => abrir(o.id)}
                  className="block text-sm underline text-left">
                  {o.folio} · {o.cliente} · {o.placa} — {o.etiqueta.toLowerCase()}
                </button>
              ))}
            </div>
          )}

          {(tablero.ordenes || []).length === 0 ? (
            <div className="rounded border p-4 text-sm">
              <p className="font-medium">No hay coches en el taller ahora mismo.</p>
              <p className="text-muted-foreground mt-1">
                Cuando llegue uno, dale a «Recibir un coche»: eso abre su orden de
                servicio y a partir de ahí el sistema lleva la reparación.
              </p>
            </div>
          ) : (tablero.ordenes || []).map(o => (
            <button key={o.id} onClick={() => abrir(o.id)}
              className="w-full rounded border p-3 flex items-center justify-between gap-3 text-left hover:bg-muted/40">
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">
                  {o.folio} · {o.cliente}
                </p>
                <p className="text-xs text-muted-foreground">
                  {o.placa}{o.marca ? ` · ${o.marca} ${o.modelo || ''}` : ''}
                  {o.total_cotizado ? ` · cotizado ${o.total_cotizado}` : ''}
                  {o.total_autorizado ? ` · autorizado ${o.total_autorizado}` : ''}
                </p>
              </div>
              <Etiqueta orden={o} />
            </button>
          ))}
        </>
      )}
    </div>
  );
}
